import { useState, useEffect, useRef } from 'react';
// Icons
import AddIcon from '@mui/icons-material/Add';
import AnnouncementIcon from '@mui/icons-material/Announcement';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';

import { Button } from "../../../common/APICommon";

export function CalendarHeader({
  todayHandlerClick,
  previousHandlerClick,
  nextHandlerClick,
  // Add Handlers
  addAnnouncementHandlerClick,
  addMemoHandlerClick,
  addEmployeeMemoHandlerClick,
  addGeneralAnnouncementHandlerClick,
  addGeneralMemoHandlerClick,
  // View All Handlers
  addAllEventsAnnouncementHandlerClick,
  addAllEventsMemoHandlerClick,
  addAllEventsEmployeeMemoHandlerClick,
  userAccess
}) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const hasAccess = userAccess && (
    userAccess.description === "Admin" ||
    userAccess.description === "HR Head" ||
    userAccess.description === "HR Assistant" ||
    userAccess.description === "HR Assistant2" ||
    userAccess.description === "HR Assistant3"
  );

  return (
    <div className="flex items-center justify-between w-full mb-4 relative p-1">

      {/* Left side: Navigation */}
      <div className="flex items-center gap-1">
        <Button
          name={"<"}
          className="text-white bg-orange-500 hover:bg-orange-600 cursor-pointer !h-8 !w-8 !p-0 flex items-center justify-center rounded-md font-bold"
          onClick={previousHandlerClick}
        />
        <Button
          name={">"}
          className="text-white bg-orange-500 hover:bg-orange-600 cursor-pointer !h-8 !w-8 !p-0 flex items-center justify-center rounded-md font-bold"
          onClick={nextHandlerClick}
        />
        <Button
          name={"TODAY"}
          className="text-white bg-blue-500 hover:bg-blue-600 cursor-pointer h-8 text-xs font-bold px-3 rounded-md ml-1"
          onClick={todayHandlerClick}
        />
      </div>

      {/* Right side: Actions Toggle */}
      {hasAccess && (
        <div ref={menuRef} className="relative">
          <button
            onClick={toggleMenu}
            className="flex items-center gap-2 bg-blue-600 text-white px-2.5 py-1.5 rounded-md hover:bg-blue-700 transition-colors border-none shadow-sm mr-1"
          >
            {isMenuOpen ? <CloseIcon className="text-sm" /> : <MenuIcon className="text-sm" />}
          </button>

          {/* Dropdown Menu */}
          {isMenuOpen && (
            <div className="absolute right-0 top-10 w-72 bg-white border border-gray-200 rounded-lg shadow-2xl z-[100] overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-100 origin-top-right">

              {/* Section: General */}
              <div className="p-2 border-b border-gray-100 bg-gray-50">
                <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider pl-2">General</span>
              </div>
              <div className="p-2 grid grid-cols-1 gap-1">
                <MenuItem
                  icon={<AnnouncementIcon fontSize="small" />}
                  label="General Announcement"
                  onClick={() => { addGeneralAnnouncementHandlerClick(); setIsMenuOpen(false); }}
                  color="bg-orange-500 hover:bg-orange-600"
                />
                <MenuItem
                  icon={<StickyNote2Icon fontSize="small" />}
                  label="General Memo"
                  onClick={() => { addGeneralMemoHandlerClick(); setIsMenuOpen(false); }}
                  color="bg-blue-500 hover:bg-blue-600"
                />
              </div>

              {/* Section: Create New */}
              <div className="p-2 border-b border-gray-100 bg-gray-50">
                <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider pl-2">Create New</span>
              </div>
              <div className="p-2 grid grid-cols-1 gap-1">
                <MenuItem
                  icon={<AddIcon fontSize="small" />}
                  label="Announcement"
                  onClick={() => { addAnnouncementHandlerClick(); setIsMenuOpen(false); }}
                  color="bg-orange-500 hover:bg-orange-600"
                />
                <MenuItem
                  icon={<AddIcon fontSize="small" />}
                  label="Memo"
                  onClick={() => { addMemoHandlerClick(); setIsMenuOpen(false); }}
                  color="bg-blue-500 hover:bg-blue-600"
                />
                <MenuItem
                  icon={<PersonOutlineIcon fontSize="small" />}
                  label="Employee Memo"
                  onClick={() => { addEmployeeMemoHandlerClick(); setIsMenuOpen(false); }}
                  color="bg-green-500 hover:bg-green-600"
                />
              </div>

              {/* Section: View Lists */}
              <div className="p-2 border-b border-gray-100 bg-gray-50">
                <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider pl-2">View Lists</span>
              </div>
              <div className="p-2 grid grid-cols-1 gap-1">
                <MenuItem
                  icon={<RemoveRedEyeIcon fontSize="small" />}
                  label="View All Announcements"
                  onClick={() => { addAllEventsAnnouncementHandlerClick(); setIsMenuOpen(false); }}
                  color="bg-cyan-500 hover:bg-cyan-600"
                />
                <MenuItem
                  icon={<RemoveRedEyeIcon fontSize="small" />}
                  label="View All Memos"
                  onClick={() => { addAllEventsMemoHandlerClick(); setIsMenuOpen(false); }}
                  color="bg-cyan-500 hover:bg-cyan-600"
                />
                <MenuItem
                  icon={<RemoveRedEyeIcon fontSize="small" />}
                  label="View All Emp. Memos"
                  onClick={() => { addAllEventsEmployeeMemoHandlerClick(); setIsMenuOpen(false); }}
                  color="bg-cyan-500 hover:bg-cyan-600"
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Helper component for menu items
function MenuItem({ icon, label, onClick, color }) {
  return (
    <div
      onClick={onClick}
      className={`flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer transition-all ${color} text-white shadow-sm`}
    >
      <div className="flex items-center justify-center">
        {icon}
      </div>
      <span className="text-xs font-medium">{label}</span>
    </div>
  )
}